<script setup>
  import { computed } from "vue";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";

  const props = defineProps({
    field: { type: Object, required: true },
    fieldData: { type: Object, default: null },
  });

  defineEmits(["state-change", "update:label", "update:description", "update:reportThemeStyleKey"]);

  // Three states: editable / non-editable (entry in editable_fields with editable true/false),
  // background (no entry in editable_fields — the entry is stripped when the state is set).
  const fieldState = computed(() => {
    if (!props.fieldData) return "background";
    return props.fieldData.editable === true ? "editable" : "non-editable";
  });

  const isEditable = computed(() => fieldState.value === "editable");

  const stateOptions = [
    { value: "editable",     label: "Editable" },
    { value: "non-editable", label: "Non-editable" },
    { value: "background",   label: "Background" },
  ];

  const styleKeyOptions = [
    { value: "cover_title",          label: "cover_title" },
    { value: "cover_subtitle",       label: "cover_subtitle" },
    { value: "section_title",        label: "section_title" },
    { value: "section_subtitle",     label: "section_subtitle" },
    { value: "content_title",        label: "content_title" },
    { value: "content_text_default", label: "content_text_default" },
    { value: "content_text_small",   label: "content_text_small" },
    { value: "content_slide_number", label: "content_slide_number" },
  ];

  const typeLabels = { text: "Text", chart: "Chart", picture: "Image", table: "Table", shape: "Shape" };
  const typeLabel = computed(() => typeLabels[props.field.type] || props.field.type || "Text");
</script>

<template>
  <div class="flex items-center gap-3 py-2">
    <!-- Element name -->
    <div class="w-24 flex-shrink-0">
      <div
        class="text-xs font-mono truncate"
        :title="field.name">
        {{ field.name }}
      </div>
    </div>

    <!-- Element type -->
    <div class="w-16 flex-shrink-0">
      <div class="text-xs text-muted-foreground">{{ typeLabel }}</div>
    </div>

    <!-- Field label (editable when state is 'editable') -->
    <div class="w-32 flex-shrink-0">
      <Input
        v-if="isEditable"
        :model-value="fieldData?.label || ''"
        :placeholder="field.name"
        class="h-8"
        @update:model-value="$emit('update:label', $event)" />
      <span
        v-else
        class="text-xs text-muted-foreground">-</span>
    </div>

    <!-- Field description (editable when state is 'editable') -->
    <div class="flex-1">
      <Input
        v-if="isEditable"
        :model-value="fieldData?.description || ''"
        class="h-8"
        @update:model-value="$emit('update:description', $event)" />
      <span
        v-else
        class="text-xs text-muted-foreground">-</span>
    </div>

    <!-- Field state: Editable / Non-editable / Background -->
    <div class="w-28 flex-shrink-0">
      <Select
        :model-value="fieldState"
        :options="stateOptions"
        class="h-8 text-xs"
        @update:model-value="$emit('state-change', $event)" />
    </div>

    <!-- Report theme style key (text fields only) -->
    <div class="w-44 flex-shrink-0">
      <Select
        v-if="fieldState !== 'background' && field.type === 'text'"
        :model-value="fieldData?.report_theme_style_key || null"
        :options="styleKeyOptions"
        filterable
        creatable
        clearable
        placeholder="—"
        class="h-8"
        @update:model-value="$emit('update:reportThemeStyleKey', $event || null)" />
      <span
        v-else
        class="text-xs text-muted-foreground">-</span>
    </div>
  </div>
</template>
